import { exactCurrencyFormatter } from "@/utils"
import { display_coding_amount } from "@/utils/transaction_amount"
import { StatusIndicators } from "./StatusIndicators.tsx"
import { DeductionListJson, TransactionJson } from "@/api"
import { CategoryCell } from "@/deduction_table"
import { TransactionPromotionField } from "./TransactionPromotionField.tsx"
import { SplitForm } from "./split_form.tsx"
import { StatusSelect } from "@/deduction_table/status_select.tsx"
import { RefreshFlag } from "@/refresh_flag.tsx"
import { Button } from "@/components/ui/button.tsx"
import { ExternalLink, FileText, Wand2 } from "lucide-react"
import { Link, useLocation } from "wouter-preact"
import { useState } from "preact/hooks"
import { format, parseISO } from "date-fns"
import { CopyableCell } from "@/components/copyable_cell.tsx"
import { CodingWizSheet } from "./CodingWizSheet.tsx"

interface InlineTransactionDetailProps {
  transaction: TransactionJson
}

function DetailField({ label, children }: { label: string; children: preact.ComponentChildren }) {
  return (
    <div>
      <div class="text-gray-500 text-sm">{label}</div>
      <div class="mt-0.5">{children}</div>
    </div>
  )
}

export function InlineTransactionDetail({ transaction }: InlineTransactionDetailProps) {
  const [, setLocation] = useLocation()
  const [codingWizOpen, setCodingWizOpen] = useState(false)

  function handleOpenTransaction() {
    setLocation(`/accounting/transaction/${transaction.id}`)
  }

  return (
    <div class="w-full h-full flex flex-col">
      {/* Top bar - CodingWiz and Open, matching the standalone accounting transaction page */}
      <div class="flex items-center justify-end gap-2 px-4 py-2 shrink-0">
        <Button variant="outline" size="sm" class="gap-1" onClick={() => setCodingWizOpen(true)}>
          <Wand2 className="h-4 w-4" />
          CodingWiz
        </Button>
        <Link href={`/deduction/${transaction.id}`}>
          <Button size="sm" class="gap-1">
            <FileText className="h-4 w-4" />
            Deduction View
          </Button>
        </Link>
        <Button variant="outline" size="sm" onClick={handleOpenTransaction}>
          <ExternalLink className="h-4 w-4 mr-1" />
          Open
        </Button>
      </div>

      <div class="flex-1 overflow-auto">
        {/* Detail form inspired by DeductionDetailSection */}
        <div class="p-4 space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <DetailField label="Invoice Number">
              <CopyableCell value={transaction.invoice_number} />
            </DetailField>
            <DetailField label="Date">
              {transaction.invoice_date ? format(parseISO(transaction.invoice_date), "PP") : "-"}
            </DetailField>
            <DetailField label="Amount">
              <span class="font-medium">
                {exactCurrencyFormatter(
                  display_coding_amount(transaction.kind, transaction.invoice_amount, transaction.discount_amount)
                )}
              </span>
            </DetailField>
            <DetailField label="Kind">
              <span class={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                transaction.kind === "repayment"
                  ? "bg-indigo-100 text-indigo-700"
                  : transaction.kind === "deduction"
                    ? "bg-orange-100 text-orange-700"
                    : transaction.kind === "invoice"
                      ? "bg-cyan-100 text-cyan-700"
                      : transaction.kind === "prepayment"
                        ? "bg-pink-100 text-pink-700"
                        : "bg-gray-100 text-gray-700"
              }`}>
                {transaction.kind || "-"}
              </span>
              {transaction.kind === "prepayment" && transaction.source === "unfi" ? (
                <div class="mt-1 text-xs text-gray-600">
                  A UNFI "PP" line: an invoice paid early, net of the terms discount — cash, not a deduction.
                </div>
              ) : null}
            </DetailField>
            <div class="col-span-2">
              <div class="text-gray-500 text-sm mb-1">Category</div>
              <CategoryCell deduction={transaction as DeductionListJson} className="mt-1" />
              {(transaction as DeductionListJson).category_description && (
                <div class="mt-2 text-sm text-gray-700">{(transaction as DeductionListJson).category_description}</div>
              )}
            </div>
            {["deduction", "invoice"].includes(transaction.kind) && transaction.kind !== "repayment" && (
              <DetailField label="Status">
                <StatusSelect
                  deduction={transaction as DeductionListJson}
                  onStatusChange={() => RefreshFlag.set((f: boolean) => !f)}
                />
              </DetailField>
            )}
          </div>

          <TransactionPromotionField transaction={transaction} />
        </div>

        <SplitForm transaction={transaction} />
        <StatusIndicators transaction={transaction} />
      </div>
      <CodingWizSheet
        open={codingWizOpen}
        onOpenChange={setCodingWizOpen}
        deductionId={transaction.id}
        onComplete={() => RefreshFlag.set((f: boolean) => !f)}
      />
    </div>
  )
}
